import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Unlock, Sparkles, Star, Share2, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import UserMenu from '@/components/UserMenu';
import Footer from '@/components/Footer';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

// =============================================================================
// NUMENAXIS SYMBOL - Brand Logo (replaces Ankh)
// =============================================================================
const NumenaxisLogo = ({ className = "w-12 h-12" }) => (
  <motion.img 
    src="/images/numenaxis-symbol-transparent.png"
    alt="NUMENAXIS"
    className={`${className} object-contain`}
    draggable={false}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  />
);

// =============================================================================
// MYSTICAL PARTICLE EFFECTS
// =============================================================================
const GoldenParticles = ({ isActive }) => {
  if (!isActive) return null;
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full"
          style={{
            background: `radial-gradient(circle, #FFD700 0%, #D4AF37 50%, transparent 100%)`,
            left: `${50 + (Math.random() - 0.5) * 60}%`,
            bottom: '20%',
          }}
          initial={{ y: 0, opacity: 0, scale: 0 }}
          animate={{
            y: -300 - Math.random() * 200,
            x: (Math.random() - 0.5) * 150,
            opacity: [0, 1, 1, 0],
            scale: [0, 1.5, 1, 0],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            delay: Math.random() * 1,
            repeat: Infinity,
            repeatDelay: Math.random() * 2,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );
};

// =============================================================================
// ANCIENT TREASURE CHEST - Luxury SVG Component
// =============================================================================
const AncientChest = ({ isOpen, isGlowing, isAnimating, onClick, canOpen }) => {
  return (
    <motion.div 
      className={`relative ${canOpen ? 'cursor-pointer' : 'cursor-default'}`}
      onClick={canOpen ? onClick : undefined}
      whileHover={canOpen ? { scale: 1.02 } : {}}
      whileTap={canOpen ? { scale: 0.98 } : {}}
      data-testid="fortune-chest"
    >
      {/* Radiant Glow Effect */}
      <AnimatePresence>
        {isGlowing && (
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="w-80 h-80 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255,215,0,0.4) 0%, rgba(212,175,55,0.2) 40%, transparent 70%)',
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Golden Particles Rising */}
      <GoldenParticles isActive={isGlowing} />
      
      <svg 
        viewBox="0 0 240 200" 
        className="w-52 h-44 sm:w-60 sm:h-48 md:w-72 md:h-56 lg:w-80 lg:h-64 relative z-10"
      >
        <defs>
          {/* Luxurious Wood Gradient */}
          <linearGradient id="chestWood" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#5D3A1A" />
            <stop offset="30%" stopColor="#8B5A2B" />
            <stop offset="70%" stopColor="#6B4423" />
            <stop offset="100%" stopColor="#3E2723" />
          </linearGradient>
          
          {/* Gold Metallic Gradient */}
          <linearGradient id="goldMetal" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFE55C" />
            <stop offset="25%" stopColor="#FFD700" />
            <stop offset="50%" stopColor="#D4AF37" />
            <stop offset="75%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#B8860B" />
          </linearGradient>
          
          {/* Inner Glow Gradient */}
          <radialGradient id="innerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFD700" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#FFA500" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#FF8C00" stopOpacity="0" />
          </radialGradient>
          
          {/* Shadow filter */}
          <filter id="chestShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="8" stdDeviation="8" floodColor="#000" floodOpacity="0.5"/>
          </filter>
          
          {/* Glow filter */}
          <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Divine Light Beam from Open Chest */}
        <AnimatePresence>
          {isOpen && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
            >
              <ellipse cx="120" cy="90" rx="70" ry="50" fill="url(#innerGlow)" />
              <motion.ellipse 
                cx="120" cy="85" rx="50" ry="35" 
                fill="#FFD700" 
                opacity="0.3"
                animate={{ 
                  ry: [35, 45, 35],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.g>
          )}
        </AnimatePresence>
        
        {/* Chest Body */}
        <g filter="url(#chestShadow)">
          <rect x="40" y="100" width="160" height="85" rx="6" fill="url(#chestWood)" />
          
          {/* Chest Body Details - Panels */}
          <rect x="50" y="110" width="60" height="65" rx="3" fill="#4A3219" opacity="0.3" />
          <rect x="130" y="110" width="60" height="65" rx="3" fill="#4A3219" opacity="0.3" />
        </g>
        
        {/* Chest Lid - Animated */}
        <motion.g
          style={{ transformOrigin: '120px 100px' }}
          animate={{ 
            rotateX: isOpen ? -75 : 0,
          }}
          transition={{ 
            duration: 1.2, 
            ease: [0.43, 0.13, 0.23, 0.96] // Custom easing for luxury feel
          }}
        >
          <path 
            d="M40 100 Q40 60 120 52 Q200 60 200 100 L200 100 L40 100 Z"
            fill="url(#chestWood)"
          />
          {/* Lid Ridge */}
          <ellipse cx="120" cy="78" rx="60" ry="10" fill="#4A3219" opacity="0.4" />
          
          {/* Gold Band on Lid */}
          <path 
            d="M45 95 Q45 62 120 55 Q195 62 195 95"
            stroke="url(#goldMetal)"
            strokeWidth="4"
            fill="none"
            filter="url(#goldGlow)"
          />
        </motion.g>
        
        {/* Decorative Gold Bands */}
        <rect x="40" y="110" width="160" height="8" fill="url(#goldMetal)" filter="url(#goldGlow)" />
        <rect x="40" y="165" width="160" height="8" fill="url(#goldMetal)" filter="url(#goldGlow)" />
        
        {/* Side Gold Bands */}
        <rect x="40" y="100" width="8" height="85" fill="url(#goldMetal)" filter="url(#goldGlow)" />
        <rect x="192" y="100" width="8" height="85" fill="url(#goldMetal)" filter="url(#goldGlow)" />
        
        {/* Corner Ornaments */}
        {[[48, 107], [184, 107], [48, 170], [184, 170]].map(([cx, cy], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r="10" fill="url(#goldMetal)" filter="url(#goldGlow)" />
            <circle cx={cx} cy={cy} r="5" fill="#3E2723" />
            <circle cx={cx} cy={cy} r="3" fill="url(#goldMetal)" />
          </g>
        ))}
        
        {/* Lock/Keyhole - Animated */}
        <AnimatePresence>
          {!isOpen && (
            <motion.g
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.5 }}
            >
              {/* Lock Plate */}
              <rect x="105" y="125" width="30" height="40" rx="4" fill="url(#goldMetal)" filter="url(#goldGlow)" />
              
              {/* Keyhole */}
              <circle cx="120" cy="137" r="6" fill="#1a1a2e" />
              <rect x="117" y="140" width="6" height="15" rx="1" fill="#1a1a2e" />
              
              {/* Lock Shimmer */}
              <motion.rect 
                x="107" y="127" width="4" height="36" rx="2" 
                fill="rgba(255,255,255,0.3)"
                animate={{ opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.g>
          )}
        </AnimatePresence>
      </svg>
    </motion.div>
  );
};

// =============================================================================
// DEITY INFO HELPER - Maps energy number to deity name and role
// =============================================================================
const getDeityInfo = (energyNumber) => {
  const deities = {
    1: { name: 'Atumas', role: 'Pradžios dievas' },
    2: { name: 'Šu', role: 'Pusiausvyros dievas' },
    3: { name: 'Nutė', role: 'Dangaus deivė' },
    4: { name: 'Gebas', role: 'Žemės dievas' },
    5: { name: 'Setas', role: 'Transformacijos dievas' },
    6: { name: 'Ozyris', role: 'Atgimimo dievas' },
    7: { name: 'Totas', role: 'Išminties dievas' },
    8: { name: 'Horas', role: 'Galios dievas' },
    9: { name: 'Maat', role: 'Teisingumo deivė' }
  };
  return deities[energyNumber] || { name: 'Nežinomas', role: 'Dievas' };
};

// =============================================================================
// ANCIENT SCROLL - Unfurling Animation
// =============================================================================
const AncientScroll = ({ fortune, isVisible, personalDay }) => {
  if (!isVisible) return null;
  
  // Extract ONLY the actual message text from backend format:
  // Backend sends: "Šiandien tavo energija sk. X, ir [Deity] ([Role]) nori pasakyti: [actual message]"
  // We want to display ONLY the [actual message] part
  const cleanMessage = fortune
    ?.replace(/^.*nori\s+pasakyti\s*:\s*/i, '') // Remove everything up to and including "nori pasakyti:"
    ?.replace(/^["„"']+|["„"']+$/g, '') // Remove surrounding quotes if any
    ?.trim() || fortune;
  
  return (
    <motion.div 
      className="mt-8 w-full max-w-xl mx-auto px-4"
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      transition={{ 
        duration: 0.8, 
        ease: [0.43, 0.13, 0.23, 0.96],
        delay: 0.3
      }}
      style={{ transformOrigin: 'top center' }}
      data-testid="fortune-scroll"
    >
      <div className="relative">
        {/* Top Scroll Rod */}
        <motion.div 
          className="relative h-10 rounded-full overflow-hidden shadow-lg"
          style={{
            background: 'linear-gradient(180deg, #8B5A2B 0%, #6B4423 50%, #8B5A2B 100%)',
          }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Rod Ends */}
          <div className="absolute left-0 top-0 w-12 h-full bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 rounded-l-full" />
          <div className="absolute right-0 top-0 w-12 h-full bg-gradient-to-l from-amber-600 via-amber-500 to-amber-600 rounded-r-full" />
          
          {/* Gold Ornament Center */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-inner" />
        </motion.div>
        
        {/* Scroll Paper */}
        <motion.div 
          className="relative overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #F5E6C8 0%, #E8D4A8 20%, #F5E6C8 50%, #E8D4A8 80%, #F5E6C8 100%)',
          }}
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Decorative Border */}
          <div className="absolute inset-0 border-x-4 border-amber-700/30 pointer-events-none" />
          
          {/* Parchment Texture Overlay */}
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />
          
          <div className="p-8 md:p-10">
            {/* Header Ornament - Elegant Golden Lines */}
            <motion.div 
              className="flex justify-center mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-3">
                <div className="h-px w-16 bg-gradient-to-r from-transparent via-amber-700/60 to-amber-700/80" />
                <div className="w-3 h-3 rounded-full bg-amber-700/60" />
                <div className="h-px w-16 bg-gradient-to-l from-transparent via-amber-700/60 to-amber-700/80" />
              </div>
            </motion.div>
            
            {/* Fortune Text - Simplified Format */}
            <motion.div 
              className="font-serif text-amber-900 leading-relaxed text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              {/* Header line */}
              <p className="text-lg md:text-xl mb-4">
                Šiandienos energija – skaičius <span className="font-bold">{personalDay}</span>.
              </p>
              {/* Message text - separate paragraph with blank line above */}
              <p className="text-xl md:text-2xl italic">
                „{cleanMessage}"
              </p>
            </motion.div>
            
            {/* Decorative Separator */}
            <motion.div 
              className="flex items-center justify-center gap-3 my-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1 }}
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-600/60" />
              <Star className="w-5 h-5 text-amber-600 fill-amber-500/30" />
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-600/60" />
            </motion.div>
            
            {/* Footer Ornament */}
            <motion.div 
              className="flex justify-center mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-600/40" />
                <div className="w-1.5 h-1.5 rounded-full bg-amber-600/30" />
                <div className="w-1 h-1 rounded-full bg-amber-600/20" />
              </div>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Bottom Scroll Rod */}
        <motion.div 
          className="relative h-10 rounded-full overflow-hidden shadow-lg"
          style={{
            background: 'linear-gradient(180deg, #8B5A2B 0%, #6B4423 50%, #8B5A2B 100%)',
          }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="absolute left-0 top-0 w-12 h-full bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 rounded-l-full" />
          <div className="absolute right-0 top-0 w-12 h-full bg-gradient-to-l from-amber-600 via-amber-500 to-amber-600 rounded-r-full" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-inner" />
        </motion.div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// MYSTICAL BACKGROUND - Stars & Nebula
// =============================================================================
const MysticalBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    {/* Deep space gradient */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-violet-950/80 to-indigo-950" />
    
    {/* Nebula effects */}
    <motion.div 
      className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)',
      }}
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.5, 0.7, 0.5],
      }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div 
      className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)',
      }}
      animate={{
        scale: [1, 1.15, 1],
        opacity: [0.4, 0.6, 0.4],
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
    />
    
    {/* Twinkling Stars */}
    {[...Array(40)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full bg-white"
        style={{
          width: Math.random() * 3 + 1,
          height: Math.random() * 3 + 1,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.2, 0.8, 0.2],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 2 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 5,
          ease: "easeInOut"
        }}
      />
    ))}
  </div>
);

// =============================================================================
// MYTH INTRO MODAL - Ancient Parchment Style
// =============================================================================
const MythIntroModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        
        {/* Modal Content - Parchment Style */}
        <motion.div 
          className="relative z-10 w-full max-w-lg mx-auto"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] }}
        >
          {/* Parchment Container */}
          <div 
            className="relative rounded-lg overflow-hidden shadow-2xl"
            style={{
              background: 'linear-gradient(180deg, #F5E6C8 0%, #E8D4A8 15%, #F5E6C8 30%, #DCC69B 50%, #F5E6C8 70%, #E8D4A8 85%, #F5E6C8 100%)',
            }}
          >
            {/* Thoth (Toto) Watermark Background */}
            <div 
              className="absolute inset-0 pointer-events-none flex items-center justify-center"
              style={{ opacity: 0.25 }}
            >
              <img 
                src="/images/thoth-watermark.png" 
                alt="" 
                className="w-[70%] h-auto object-contain"
                style={{ 
                  filter: 'sepia(40%) contrast(90%)',
                  mixBlendMode: 'multiply'
                }}
              />
            </div>
            
            {/* Parchment Texture Overlay */}
            <div 
              className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />
            
            {/* Decorative Border */}
            <div className="absolute inset-2 border-2 border-amber-700/30 rounded pointer-events-none" />
            <div className="absolute inset-4 border border-amber-700/20 rounded pointer-events-none" />
            
            {/* Close Button (X) - Top Right Corner */}
            <motion.button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center text-amber-800/60 hover:text-amber-800 hover:bg-amber-900/10 transition-colors"
              title="Uždaryti"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <X className="w-5 h-5" />
            </motion.button>
            
            {/* Content */}
            <div className="relative p-6 md:p-8">
              {/* Header - NUMENAXIS Symbol */}
              <motion.div 
                className="flex justify-center mb-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-700/60" />
                  <NumenaxisLogo className="w-10 h-10" />
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-700/60" />
                </div>
              </motion.div>
              
              {/* Title */}
              <motion.h2 
                className="text-center font-display text-xl md:text-2xl mb-4"
                style={{ color: '#5D3A1A' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Sėkmės Skrynios Legenda
              </motion.h2>
              
              {/* Myth Text */}
              <motion.div 
                className="space-y-4 text-sm md:text-base leading-relaxed"
                style={{ color: '#4A3520' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <p className="first-letter:text-3xl first-letter:font-display first-letter:text-amber-800 first-letter:float-left first-letter:mr-2 first-letter:leading-none">
                  Senovės papirusai pasakoja, kad išminties dievas Totas surinko devynias Visatos vibracijas ir paslėpė jas auksiniame inde. Jis tikėjo, kad žmogaus siela yra lyg upė, kuri kasdien teka vis kitu ritmu.
                </p>
                <p>
                  Šiandien Sėkmės skrynia atsiveria pagal tavo asmeninį kodą. Tai ne paprasti žodžiai, o Maat (tiesos ir pusiausvyros) balsas, padedantis šiuolaikiniam žmogui rasti savo kryptį kasdienybės labirinte. Kiekviena diena turi savo dieviškąjį virpesį – priimk jį kaip dovaną.
                </p>
              </motion.div>
              
              {/* Footer Ornament */}
              <motion.div 
                className="flex justify-center mt-6 mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <Star className="w-3 h-3 text-amber-700/50" />
                  <div className="h-px w-20 bg-gradient-to-r from-amber-700/60 via-amber-700/30 to-amber-700/60" />
                  <Star className="w-3 h-3 text-amber-700/50" />
                </div>
              </motion.div>
              
              {/* CTA Button */}
              <motion.div 
                className="flex justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <Button
                  onClick={onClose}
                  className="bg-gradient-to-r from-amber-700 to-amber-800 hover:from-amber-800 hover:to-amber-900 text-amber-100 font-semibold px-6 py-3 text-sm md:text-base shadow-lg"
                  data-testid="myth-intro-button"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Atverti savo dienos virpesį
                </Button>
              </motion.div>
            </div>
          </div>
          
          {/* Decorative Corner Flourishes */}
          <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-amber-500/50 rounded-tl" />
          <div className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-amber-500/50 rounded-tr" />
          <div className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-amber-500/50 rounded-bl" />
          <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-amber-500/50 rounded-br" />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// =============================================================================
// MYTH INFO BUTTON - Small "i" icon to re-read the myth
// =============================================================================
const MythInfoButton = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-amber-500/20 hover:bg-amber-500/40 flex items-center justify-center text-amber-400 hover:text-amber-300 transition-colors"
    title="Apie Sėkmės Skrynią"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
    </svg>
  </motion.button>
);

// =============================================================================
// GODS OF WISDOM DATA
// =============================================================================
const GODS_DATA = [
  {
    number: 1,
    nameEn: 'Atum',
    nameLt: 'Atumas',
    vibration: 'Pradžia, vienybė, pirminis impulsas, „Aš esu".',
    description: 'Atumas yra pirminis kūrėjas, iškilęs iš Nun vandenų. Jis sukūrė save pats. Numerologinis 1 yra lyderis, pradininkas, taškas, nuo kurio prasideda visa visata.'
  },
  {
    number: 2,
    nameEn: 'Shu',
    nameLt: 'Šu',
    vibration: 'Dualumas, partnerystė, erdvė tarp dviejų polių, pusiausvyra.',
    description: 'Šu yra oro dievas, kuris atskyrė Dangų (Nutę) nuo Žemės (Gebo). Skaičius 2 numerologijoje visada yra apie santykį tarp dviejų. Šu yra ta erdvė, kurioje vyksta gyvenimas tarp polių.'
  },
  {
    number: 3,
    nameEn: 'Nut',
    nameLt: 'Nutė',
    vibration: 'Saviraiška, kūryba, plėtra, begalybė.',
    description: 'Nutė yra dangaus skliautas, nusėtas milijonais žvaigždžių. 3 numerologijoje yra kūrybinis vaisius (1+2=3). Tai gausos ir ekspansijos energija, kurią puikiai atspindi beribis žvaigždėtas dangus.'
  },
  {
    number: 4,
    nameEn: 'Geb',
    nameLt: 'Gebas',
    vibration: 'Stabilumas, pamatas, struktūra, darbas.',
    description: 'Gebas yra žemės dievas. Žemė yra mūsų pamatas, kvadratas, keturios pasaulio pusės. 4 yra stabilumo skaičius. Gebas duoda tvirtumą ir materialią formą viskam, kas gyva.'
  },
  {
    number: 5,
    nameEn: 'Set',
    nameLt: 'Setas',
    vibration: 'Laisvė, pokyčiai, chaosas, transformacija, penki pojūčiai.',
    description: 'Setas yra dykumų ir audrų dievas. Jis sugriauna tai, kas sena, kad atsirastų vietos naujam. 5 yra pats judriausias skaičius, nemėgstantis rėmų. Tai energija, kuri neleidžia sistemai sustingti.'
  },
  {
    number: 6,
    nameEn: 'Osiris',
    nameLt: 'Ozyris',
    vibration: 'Harmonija, šeima, globa, atsakomybė, grožis.',
    description: 'Ozyris yra tas, kuris mokė žmones žemdirbystės, įstatymų ir tvarkos. Jis simbolizuoja teisingą valdovą, tėvą. 6 yra šeimos ir bendruomenės harmonijos skaičius. Jis saugo ir globoja gyvybės ciklą.'
  },
  {
    number: 7,
    nameEn: 'Thoth',
    nameLt: 'Totas',
    vibration: 'Analizė, mokslas, magija, vienatvė, tiesos ieškojimas.',
    description: 'Totas yra išminties, rašto ir magijos dievas. 7 numerologijoje yra mistiko ir mąstytojo skaičius. Tai gilus nardymas į žinias, ko Totas mokė dievus ir žmones.'
  },
  {
    number: 8,
    nameEn: 'Horus',
    nameLt: 'Horas',
    vibration: 'Galia, autoritetas, pergalė, kova už teisingumą, materialus triumfas.',
    description: 'Horas yra nugalėtojas, kuris atsikovojo sostą iš Seto. 8 yra jėgos ir pasiekimų skaičius. Tai akis, kuri mato viską, ir ranka, kuri valdo teisingai, pasiekdama rezultatą šiame pasaulyje.'
  },
  {
    number: 9,
    nameEn: 'Maat',
    nameLt: 'Maat',
    vibration: 'Pabaiga, universali meilė, dvasinė išmintis, aukščiausia tvarka.',
    description: 'Maat yra tiesos ir pusiausvyros deivė. 9 yra paskutinis vienaženklis skaičius, simbolizuojantis ciklo užbaigimą ir aukščiausią sąmonės lygį. Po visų kovų ir kūrybos lieka tik Maat svarstyklės — absoliuti tiesa.'
  }
];

// =============================================================================
// GODS WISDOM BUTTON - Key icon to open the Gods Grid
// =============================================================================
const GodsWisdomButton = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    className="absolute z-20 w-8 h-8 rounded-full bg-amber-500/20 hover:bg-amber-500/40 flex items-center justify-center text-amber-400 hover:text-amber-300 transition-colors
               top-14 right-4
               md:top-4 md:right-14"
    title="Išminties Dievai"
    data-testid="gods-wisdom-button"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
    </svg>
  </motion.button>
);

// =============================================================================
// GOD DETAIL MODAL - Parchment style detail view for each god
// =============================================================================
const GodDetailModal = ({ god, isOpen, onClose }) => {
  if (!isOpen || !god) return null;
  
  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[110] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        
        {/* Modal Content - Parchment Style */}
        <motion.div 
          className="relative z-10 w-full max-w-lg mx-auto"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, ease: [0.43, 0.13, 0.23, 0.96] }}
        >
          {/* Parchment Container */}
          <div 
            className="relative rounded-lg overflow-hidden shadow-2xl"
            style={{
              background: 'linear-gradient(180deg, #F5E6C8 0%, #E8D4A8 15%, #F5E6C8 30%, #DCC69B 50%, #F5E6C8 70%, #E8D4A8 85%, #F5E6C8 100%)',
            }}
          >
            {/* Watermark - God-specific image filling entire parchment */}
            <div 
              className="absolute inset-0 pointer-events-none overflow-hidden"
              style={{ opacity: 0.25 }}
            >
              <img 
                src={`/images/gods/${god.nameEn.toLowerCase()}.png`}
                alt=""
                className="w-full h-full object-cover object-center"
                style={{ 
                  filter: 'sepia(30%) contrast(90%)',
                  mixBlendMode: 'multiply'
                }}
                onError={(e) => {
                  // Fallback to number if image doesn't exist
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
              {/* Fallback number (hidden by default) */}
              <div className="absolute inset-0 items-center justify-center hidden">
                <span className="text-[120px] text-amber-900/30 font-serif">{god.number}</span>
              </div>
            </div>
            
            {/* Parchment Texture Overlay */}
            <div 
              className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />
            
            {/* Decorative Border */}
            <div className="absolute inset-2 border-2 border-amber-700/30 rounded pointer-events-none" />
            <div className="absolute inset-4 border border-amber-700/20 rounded pointer-events-none" />
            
            {/* Close Button */}
            <motion.button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center text-amber-800/60 hover:text-amber-800 hover:bg-amber-900/10 transition-colors"
              title="Uždaryti"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <X className="w-5 h-5" />
            </motion.button>
            
            {/* Content */}
            <div className="relative p-6 md:p-8">
              {/* Header with NUMENAXIS Symbol */}
              <motion.div 
                className="flex justify-center mb-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-px w-8 bg-gradient-to-r from-transparent to-amber-700/60" />
                  <NumenaxisLogo className="w-8 h-8" />
                  <div className="h-px w-8 bg-gradient-to-l from-transparent to-amber-700/60" />
                </div>
              </motion.div>
              
              {/* Title */}
              <motion.h2 
                className="text-center font-serif text-xl md:text-2xl text-amber-900 mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Kodėl {god.nameEn} ir skaičius {god.number}?
              </motion.h2>
              
              {/* Subtitle - Lithuanian name */}
              <motion.p
                className="text-center text-amber-800/70 text-sm mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
              >
                {god.nameLt} — Skaičius {god.number}
              </motion.p>
              
              {/* Divider */}
              <div className="flex justify-center mb-4">
                <div className="h-px w-24 bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />
              </div>
              
              {/* Vibration */}
              <motion.div
                className="mb-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="text-amber-800 font-semibold text-sm mb-1">Vibracija:</h3>
                <p className="text-amber-900/80 text-sm italic">{god.vibration}</p>
              </motion.div>
              
              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="text-amber-800 font-semibold text-sm mb-1">Aprašymas:</h3>
                <p className="text-amber-900/90 text-sm leading-relaxed">{god.description}</p>
              </motion.div>
              
              {/* Back to Grid Button */}
              <motion.div
                className="mt-6 flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="border-amber-700/40 text-amber-800 hover:bg-amber-100/50"
                >
                  ← Grįžti į sąrašą
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// =============================================================================
// GODS GRID MODAL - 3x3 grid of Egyptian gods
// =============================================================================
const GodsGridModal = ({ isOpen, onClose }) => {
  const [selectedGod, setSelectedGod] = useState(null);
  
  if (!isOpen) return null;
  
  // If a god is selected, show detail view
  if (selectedGod) {
    return (
      <GodDetailModal 
        god={selectedGod} 
        isOpen={true} 
        onClose={() => setSelectedGod(null)} 
      />
    );
  }
  
  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        
        {/* Modal Content */}
        <motion.div 
          className="relative z-10 w-full max-w-md mx-auto"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, ease: [0.43, 0.13, 0.23, 0.96] }}
        >
          {/* Card Container */}
          <div 
            className="relative rounded-xl overflow-hidden shadow-2xl border border-amber-500/30"
            style={{
              background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
            }}
          >
            {/* Close Button */}
            <motion.button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
              title="Uždaryti"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <X className="w-5 h-5" />
            </motion.button>
            
            {/* Content */}
            <div className="p-6">
              {/* Header */}
              <motion.div 
                className="text-center mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex justify-center mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-px w-8 bg-gradient-to-r from-transparent to-amber-500/60" />
                    <NumenaxisLogo className="w-8 h-8" />
                    <div className="h-px w-8 bg-gradient-to-l from-transparent to-amber-500/60" />
                  </div>
                </div>
                <h2 className="font-serif text-xl text-amber-400 mb-1">Išminties Dievai</h2>
                <p className="text-foreground/60 text-xs">Pasirinkite dievybę ir sužinokite jos numerologinę prasmę</p>
              </motion.div>
              
              {/* 3x3 Grid */}
              <motion.div 
                className="grid grid-cols-3 gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {GODS_DATA.map((god, index) => (
                  <motion.button
                    key={god.number}
                    onClick={() => setSelectedGod(god)}
                    className="relative p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/15 hover:border-amber-500/40 transition-all group"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-testid={`god-button-${god.number}`}
                  >
                    {/* Number Badge */}
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-black text-xs font-bold flex items-center justify-center">
                      {god.number}
                    </div>
                    
                    {/* Names */}
                    <div className="text-center">
                      <p className="text-amber-300 font-medium text-sm group-hover:text-amber-200">{god.nameEn}</p>
                      <p className="text-foreground/50 text-xs">({god.nameLt})</p>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
              
              {/* Footer hint */}
              <motion.p
                className="text-center text-foreground/40 text-xs mt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                Paspauskite ant dievo vardo, kad sužinotumėte daugiau
              </motion.p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// =============================================================================
// MAIN FORTUNE CHEST PAGE
// =============================================================================
export default function FortuneChestPage() {
  const navigate = useNavigate();
  const { user, isVIP } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [chestState, setChestState] = useState('locked'); // locked, opening, open
  const [fortune, setFortune] = useState(null);
  const [practice, setPractice] = useState(null);
  const [showScroll, setShowScroll] = useState(false);
  const [showMythIntro, setShowMythIntro] = useState(false);
  const [showMythInfo, setShowMythInfo] = useState(false);
  const [showGodsGrid, setShowGodsGrid] = useState(false);

  // Check if user has seen the myth intro
  useEffect(() => {
    const hasSeenMythIntro = localStorage.getItem('numenaxis_myth_intro_seen');
    if (!hasSeenMythIntro && isVIP) {
      setShowMythIntro(true);
    }
  }, [isVIP]);

  const handleMythIntroDismiss = () => {
    localStorage.setItem('numenaxis_myth_intro_seen', 'true');
    setShowMythIntro(false);
  };

  // Fetch fortune status on load
  useEffect(() => {
    if (user?.email) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/fortune/status?email=${encodeURIComponent(user.email)}`
      );
      setStatus(response.data);
      
      // If user already opened today, show the last fortune
      if (!response.data.canOpen && response.data.lastFortune) {
        setFortune(response.data.lastFortune);
        setChestState('open');
        setShowScroll(true);
      }
    } catch (error) {
      console.error('Fortune status error:', error);
      toast.error('Nepavyko gauti skrynios būsenos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChest = async () => {
    if (!user?.email) {
      toast.error('Prašome prisijungti, kad atrakintumėte skrynią');
      return;
    }
    
    if (!status?.canOpen) {
      toast.info(status?.message || 'Skrynia dar užrakinta');
      return;
    }
    
    setOpening(true);
    setChestState('opening');
    
    // Wait for lid opening animation
    await new Promise(resolve => setTimeout(resolve, 1200));
    setChestState('open');
    
    // Wait for glow effect to build
    await new Promise(resolve => setTimeout(resolve, 800));
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/fortune/open?email=${encodeURIComponent(user.email)}`
      );
      
      if (response.data.success) {
        setFortune(response.data.fortuneContent);
        setPractice(response.data.practice);
        
        // Show scroll with unfurling animation
        await new Promise(resolve => setTimeout(resolve, 600));
        setShowScroll(true);
        
        // Update status
        setStatus(prev => ({
          ...prev,
          canOpen: false,
          lastFortune: response.data.fortuneContent
        }));
      }
    } catch (error) {
      console.error('Fortune open error:', error);
      toast.error(error.response?.data?.detail || 'Klaida atidarant skrynią');
      setChestState('locked');
    } finally {
      setOpening(false);
    }
  };

  const handleShare = () => {
    // Simplified share text without deity description
    const text = `✨ Mano šiandienos palinkėjimas iš NUMENAXIS:\n\nŠiandienos energija – skaičius ${status?.personalDay}.\n\n„${fortune}"\n\n🗝️ Atrask savo sėkmės skrynią: numenaxis.com`;
    
    // Always try Web Share API first (opens native share sheet on mobile)
    if (navigator.share) {
      navigator.share({
        title: 'NUMENAXIS Sėkmės Skrynia',
        text: text,
        url: 'https://numenaxis.com'
      }).catch((err) => {
        // User cancelled or share failed - fallback to clipboard
        if (err.name !== 'AbortError') {
          copyTextToClipboard(text);
        }
      });
    } else {
      // Desktop fallback - copy to clipboard
      copyTextToClipboard(text);
    }
  };

  const copyTextToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          toast.success('Nukopijuota į iškarpinę!');
        })
        .catch(() => {
          fallbackCopy(text);
        });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Nukopijuota į iškarpinę!');
    } catch (err) {
      toast.error('Nepavyko nukopijuoti');
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-violet-950 to-slate-950 flex items-center justify-center">
        <motion.div 
          className="flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-amber-400/80 text-sm">Kraunama...</p>
        </motion.div>
      </div>
    );
  }

  // Not Logged In State
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col overflow-x-hidden max-w-full">
        <MysticalBackground />
        
        <header className="relative z-50 p-4 flex justify-between items-center bg-[#0f0f23]/95 backdrop-blur-sm">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Grįžti
          </Button>
          <UserMenu />
        </header>
        
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Lock className="w-24 h-24 text-amber-500/30 mx-auto mb-6" />
            <h1 className="font-display text-3xl md:text-4xl text-amber-400 mb-4">
              Tavo Sėkmės Skrynia
            </h1>
            <p className="text-foreground/60 max-w-md mx-auto mb-8">
              Prašome prisijungti, kad atrakintumėte savo sėkmės skrynią ir gautumėte kasdienį dvasinį palinkėjimą.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold px-8"
              data-testid="login-redirect-button"
            >
              Prisijungti
            </Button>
          </motion.div>
        </main>
        
        <Footer />
      </div>
    );
  }

  // Main Logged In View
  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden max-w-full">
      <MysticalBackground />
      
      {/* Myth Intro Modal - Shows on first visit */}
      <MythIntroModal 
        isOpen={showMythIntro} 
        onClose={handleMythIntroDismiss} 
      />
      
      {/* Myth Info Modal - Can be reopened anytime */}
      <MythIntroModal 
        isOpen={showMythInfo} 
        onClose={() => setShowMythInfo(false)} 
      />
      
      {/* Gods of Wisdom Grid Modal */}
      <GodsGridModal 
        isOpen={showGodsGrid} 
        onClose={() => setShowGodsGrid(false)} 
      />
      
      {/* Header - Fixed with solid background for /sekmes-skrynia */}
      <header className="relative z-50 p-4 flex justify-between items-center bg-[#0f0f23]/95 backdrop-blur-sm">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Grįžti
        </Button>
        <UserMenu />
      </header>
      
      {/* Main Content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-start p-4 pt-4 md:pt-8 lg:pt-6">
        {/* Info Button - Always visible in corner */}
        <MythInfoButton onClick={() => setShowMythInfo(true)} />
        
        {/* Gods of Wisdom Button - Key icon next to info button */}
        <GodsWisdomButton onClick={() => setShowGodsGrid(true)} />
        
        {/* Title Section - Compact on desktop to reveal gods' heads */}
        <motion.div 
          className="text-center mb-6 md:mb-10 lg:mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-display text-2xl md:text-4xl lg:text-3xl text-amber-400 mb-4 lg:mb-2">
            Tavo Sėkmės Skrynia
          </h1>
          
          {/* Mobile: 4 lines with breaks | Desktop (lg+): Single horizontal line, smaller font */}
          <p className="text-foreground/60 max-w-md lg:max-w-none mx-auto text-sm md:text-base lg:text-sm leading-relaxed">
            <span className="lg:hidden">
              Kasdien atverk skrynią<br />
              ir priimk<br />
              Visatos<br />
              palinkėjimą
            </span>
            <span className="hidden lg:inline">
              Kasdien atverk skrynią ir priimk Visatos palinkėjimą
            </span>
          </p>
          
        </motion.div>
        
        {/* Council of the Gods - Three Layer Composition */}
        {/* STABLE VERSION: Separate positioning for Mobile (<768px) and Desktop (≥768px) */}
        {/* Desktop: taller container to show full god figures including heads */}
        <motion.div 
          className="relative w-full max-w-4xl mx-auto overflow-hidden h-[350px] lg:h-[450px]"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* === LAYER 1: MAAT (Left) === */}
          {/* 
            MOBILE (0-767px): left-[5%], bottom-[80px], w-36, z-[1] BEHIND chest
            DESKTOP (lg+): left-[10%], bottom-[10px], w-60, z-[3] in front of chest
          */}
          <motion.div
            className="absolute pointer-events-none
                       z-[1] left-[5%] bottom-[80px]
                       md:left-[18%] md:bottom-[10px]
                       lg:z-[3] lg:left-[10%] lg:bottom-[10px]"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="relative w-36 md:w-60">
              {/* Ethereal Glow Effect */}
              <div 
                className="absolute inset-0 blur-xl rounded-full"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(251, 191, 36, 0.25) 0%, rgba(217, 119, 6, 0.1) 50%, transparent 70%)',
                  transform: 'scale(1.4)'
                }}
              />
              {/* Maat Image - 70% opacity, spirit guardian */}
              <motion.img 
                src="/images/maat-new.png" 
                alt="Maat - Teisingumo deivė" 
                className="relative w-full h-auto object-contain drop-shadow-[0_0_20px_rgba(251,191,36,0.3)]"
                style={{ 
                  opacity: 0.7,
                  filter: 'brightness(1.15) contrast(1.05)'
                }}
                animate={{
                  y: [0, -4, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </motion.div>

          {/* === LAYER 2: CHEST (Center) === */}
          {/* Fixed-height container with items-end keeps chest base locked at bottom */}
          {/* Mobile: z-[3] on top | Desktop: z-[2] behind gods */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-[3] lg:z-[2] flex flex-col items-center h-full justify-end">
            {/* Fixed-height chest wrapper - prevents vertical shift when chest opens */}
            <div className="h-[180px] sm:h-[200px] md:h-[260px] flex items-end justify-center">
              <AncientChest 
                isOpen={chestState === 'open' || chestState === 'opening'}
                isGlowing={chestState === 'opening' || (chestState === 'open' && !showScroll)}
                isAnimating={chestState === 'opening'}
                onClick={handleOpenChest}
                canOpen={status?.canOpen}
              />
            </div>
          
            {/* Action Button / Status - FIXED HEIGHT to prevent layout shift */}
            <div className="h-[80px] flex items-start justify-center pt-6">
              {status?.canOpen ? (
                <Button
                  onClick={handleOpenChest}
                  disabled={opening}
                  className="bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 hover:from-amber-600 hover:via-amber-500 hover:to-amber-600 text-black font-semibold px-10 py-6 text-lg shadow-lg shadow-amber-500/25 transition-all duration-300"
                  data-testid="open-chest-button"
                >
                  {opening ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                      </motion.div>
                      Atidaroma...
                    </>
                  ) : (
                    <>
                      <Unlock className="w-5 h-5 mr-2" />
                      Atidaryti skrynią
                    </>
                  )}
                </Button>
              ) : (
                chestState !== 'open' && (
                  <div className="flex flex-col items-center gap-3">
                    <Lock className="w-8 h-8 text-foreground/40" />
                    <p className="text-foreground/60 text-sm max-w-xs text-center">{status?.message}</p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* === LAYER 3: THOTH (Right, IN FRONT of chest) === */}
          {/* 
            MOBILE (0-767px): right-[-4%], bottom-[60px], w-40 - moved ~25px more right
            DESKTOP (lg+): right-[8%], bottom-[10px], w-60
          */}
          <motion.div
            className="absolute z-[3] pointer-events-none
                       right-[-4%] bottom-[60px]
                       md:right-[15%] md:bottom-[-5px]
                       lg:right-[8%] lg:bottom-[10px]"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="relative w-40 md:w-72 lg:w-60">
              {/* Ethereal Glow Effect */}
              <div 
                className="absolute inset-0 blur-xl rounded-full"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(100, 180, 255, 0.2) 0%, rgba(59, 130, 246, 0.1) 50%, transparent 70%)',
                  transform: 'scale(1.4)'
                }}
              />
              {/* Thoth Image - 70% opacity, taller than Maat, spirit guardian */}
              <motion.img 
                src="/images/thoth-new.png" 
                alt="Thoth - Išminties dievas" 
                className="relative w-full h-auto object-contain drop-shadow-[0_0_20px_rgba(100,180,255,0.3)]"
                style={{ 
                  opacity: 0.7,
                  filter: 'brightness(1.15) contrast(1.05)'
                }}
                animate={{
                  y: [0, -4, 0],
                }}
                transition={{
                  duration: 5.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />
            </div>
          </motion.div>
        </motion.div>
        
        {/* Fortune Scroll */}
        <AncientScroll 
          fortune={fortune}
          isVisible={showScroll}
          personalDay={status?.personalDay || 1}
        />
        
        {/* Share Button */}
        <AnimatePresence>
          {showScroll && (
            <motion.div 
              className="mt-8 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6 }}
            >
              <Button
                variant="outline"
                onClick={handleShare}
                className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 gap-2"
                data-testid="share-fortune-button"
              >
                <Share2 className="w-4 h-4" />
                Dalintis palinkėjimu
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      
      <Footer />
    </div>
  );
}
